import { useQuery } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import { useTranslation } from 'react-i18next';

import { getCoachDashboard } from '@/api/coach';
import { GreetingHeader } from '@/components/home/GreetingHeader';
import { GoalStrip } from '@/components/home/GoalStrip';
import { KpiRow } from '@/components/home/KpiRow';
import { ClubNumbers } from '@/components/home/ClubNumbers';
import { WeekAhead } from '@/components/home/WeekAhead';
import { ActionCenter } from '@/components/home/ActionCenter';
import { Skeleton } from '@/components/ui/skeleton';

// HomeDashboard — the daily command center shown once core setup is done. It
// composes independent widgets, each owning its own query so one slow or failed
// source never blanks the page. The coach dashboard query drives the greeting's
// to-do count, the goal strip, and the action center; the KPI row and upcoming
// agenda fetch their own data.
export function HomeDashboard({ slug }: { slug: string }) {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;

  const coach = useQuery({
    queryKey: ['coach-dashboard', slug],
    queryFn: () => getCoachDashboard(token, slug),
    enabled: !!token && !!slug,
  });

  const data = coach.data;
  const goals = data?.goals ?? [];
  const cards = data?.cards ?? [];
  const starters = data?.starters ?? [];

  const goalTitle = (key: string) =>
    key ? t(`coach.goals.catalog.${key}.title`) : t('coach.dashboard.general');

  return (
    <div className="flex flex-col gap-6">
      {/* Greeting hero over a soft pastel blob — the one decorative flourish. */}
      <div className="relative isolate">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-10 -right-6 -z-10 h-40 w-64 rounded-full opacity-50 blur-3xl"
          style={{
            background:
              'radial-gradient(circle at 30% 30%, var(--aqua), transparent 60%), radial-gradient(circle at 70% 70%, var(--pink), transparent 60%)',
          }}
        />
        <GreetingHeader slug={slug} todoCount={cards.length} />
      </div>

      {/* Goal strip waits for the coach query so it never flashes the empty
          "set goals" state before data arrives. */}
      {coach.isLoading ? (
        <Skeleton className="h-9 w-64" />
      ) : (
        <GoalStrip slug={slug} goals={goals} hasGoals={data?.hasGoals ?? false} />
      )}

      {/* The KPI tiles and the to-do list share the main (left) column; the
          week-ahead sits in a sidebar next to them, sized by its own content
          (500px floor) rather than stretched to the left column's height.
          Stacks on mobile; two columns from lg up. */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="flex flex-col gap-6 lg:col-span-2">
          <KpiRow slug={slug} token={token} />
          {coach.isLoading ? (
            <div className="flex flex-col gap-3">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : (
            <ActionCenter
              slug={slug}
              cards={cards}
              starters={starters}
              goals={goals}
              goalTitle={goalTitle}
            />
          )}
        </div>
        <div className="lg:col-span-1">
          <WeekAhead slug={slug} token={token} />
        </div>
      </div>

      {/* The board's monthly figures, full width below the day's work: an admin
          acts on the to-do list daily and reports these monthly, so they sit
          after everything actionable rather than competing with it. */}
      <ClubNumbers slug={slug} token={token} />
    </div>
  );
}
